import { createContext, useContext } from 'react';

/**
 * JOB-13 (R3): every section of the one-page builder saves the same job, and each save moves
 * the job's version on. Two sections saving at once would each send the version they last
 * saw and one would be refused as stale. The lane runs a job's saves one at a time and hands
 * each the version the last one returned, so they never race into a 409 (CLAUDE.md §5a).
 */
export interface SaveLane {
  run<T extends { readonly version: number }>(save: (version: number) => Promise<T>): Promise<T>;
  /** A save from elsewhere (a realtime refetch, another tab) moved the job on: follow it while idle. */
  follow(version: number): void;
  /** Resolves once every save already in the lane has finished ("Open job" waits for it). */
  idle(): Promise<void>;
  /** The version the next save will send. */
  version(): number;
}

export function createSaveLane(initialVersion: number): SaveLane {
  let version = initialVersion;
  let waiting = 0;
  let tail: Promise<unknown> = Promise.resolve();
  return {
    run<T extends { readonly version: number }>(save: (version: number) => Promise<T>): Promise<T> {
      waiting += 1;
      const next = tail
        .then(() => save(version))
        .then((result) => {
          version = result.version;
          return result;
        })
        .finally(() => {
          waiting -= 1;
        });
      // A failed save is its caller's to report; the lane carries on with the next.
      tail = next.catch(() => undefined);
      return next;
    },
    follow(next: number): void {
      if (waiting === 0) version = next;
    },
    async idle(): Promise<void> {
      await tail;
    },
    version(): number {
      return version;
    },
  };
}

export const SaveLaneContext = createContext<SaveLane | null>(null);

export function useSaveLane(): SaveLane | null {
  return useContext(SaveLaneContext);
}
